import React, { useEffect, useState } from 'react';
import { DatePicker, Descriptions, Drawer, Tabs, Tag } from 'antd';
import { useCurrentRider } from '../../hooks/useCurrentRider';
import TripsHistory from './TripsHistory';
import { CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useGetTripsSummaryByPassengerIdQuery } from '../../../../GraphQL/hooks/useGetTripsSummaryByPassengerIdQuery';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const TripStatisticCard = ({ icon, label, count, color = '#1677ff' }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            borderRadius: '10px',
            padding: '12px 16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            marginBottom: '12px',
            maxWidth: '250px',
        }}
    >
        <div style={{ fontSize: 32, color: color, marginRight: 12 }}>{icon}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>{label}</div>
            <div style={{ fontSize: '16px', fontWeight: 600, color }}>{count}</div>
        </div>
    </div>
);

const RiderDetailsDrawer = ({ open, onClose }) => {
    const { currentRider } = useCurrentRider();
    if (!currentRider) return null;
    const [activeTab, setActiveTab] = useState('1');

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf('month'),
        dayjs().endOf('month'),
    ]);

    const { data: tripsSummary } = useGetTripsSummaryByPassengerIdQuery({
        id: currentRider._id,
        startDate: dateRange[0].toDate(),
        endDate: dateRange[1].toDate(),
    });

    useEffect(() => {
        if (!open) {
            setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
            setActiveTab('1');
        }
    }, [open]);

    const completedTrips = tripsSummary?.completedTrips ?? 0;
    const canceledTrips = tripsSummary?.canceledTrips ?? 0;
    const couponsUsed = tripsSummary?.couponsUsed ?? [];

    return (
        <Drawer
            title={`Rider Details - ${currentRider.fullName}`}
            placement="right"
            width={600}
            onClose={onClose}
            open={open}
        >
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                items={[
                    {
                        key: '1',
                        label: 'Profile',
                        children: (
                            <div>
                                {/* Date Range Filter */}
                                <div style={{ marginBottom: 16 }}>
                                    <RangePicker
                                        value={dateRange}
                                        onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                                    />
                                </div>

                                {/* Trip Statistics */}
                                <h3 style={{ marginBottom: '16px' }}>Trip Statistics</h3>
                                <TripStatisticCard
                                    icon={<CheckCircleOutlined />}
                                    label="Completed Trips"
                                    count={completedTrips}
                                    color="#52c41a"
                                />
                                <TripStatisticCard
                                    icon={<CloseCircleOutlined />}
                                    label="Cancelled Trips"
                                    count={canceledTrips}
                                    color="#ff4d4f"
                                />

                                <Descriptions
                                    title="Rider Information"
                                    bordered
                                    column={1}
                                    size="small"
                                    style={{ background: '#fff', borderRadius: '8px', padding: '12px' }}
                                >
                                    <Descriptions.Item label="Country">
                                        <Tag color="blue">{currentRider.country}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Wallet Balance">
                                        <Tag color="green">{currentRider.walletBalance} TND</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Coupons Used">
                                        {couponsUsed.length > 0 ? (
                                            couponsUsed.map((coupon) => (
                                                <Tag key={coupon} color="purple">
                                                    {coupon}
                                                </Tag>
                                            ))
                                        ) : (
                                            <Tag color="default">None</Tag>
                                        )}
                                    </Descriptions.Item>
                                </Descriptions>
                            </div>
                        ),
                    },
                    {
                        key: '2',
                        label: 'Trips History',
                        children: (
                            <TripsHistory riderId={currentRider?._id} open={open} handleViewInvoice={(trip) => {}} />
                        ),
                    },
                ]}
            />
        </Drawer>
    );
};

export default RiderDetailsDrawer;
